import React, { useState } from "react";
import { Lock, Paperclip } from "lucide-react";
import { fonts } from "../theme";
import { openPrivateProof } from "../api";

function scoreColor(val, theme) {
  const n = Number(val) || 0;
  if (n >= 8) return theme.key === "light" ? "#059669" : "#34D399";
  if (n >= 6) return theme.key === "light" ? "#0369A1" : "#38BDF8";
  if (n >= 4) return theme.key === "light" ? "#B45309" : "#FBBF24";
  return theme.key === "light" ? "#DC2626" : "#F87171";
}

export default function PrivateRatingsOnFile({ ratings, criteria, title, emptyText, theme, heading }) {
  const list = Array.isArray(ratings) ? ratings : [];
  const isLight = theme?.key === "light";
  const headingText = heading || "Private ratings on file";
  const [proofError, setProofError] = useState("");
  const [proofBusy, setProofBusy] = useState("");

  const viewProof = async (filename) => {
    setProofError("");
    setProofBusy(filename);
    try {
      await openPrivateProof(filename);
    } catch (err) {
      setProofError(err.message || "Could not open private proof.");
    } finally {
      setProofBusy("");
    }
  };

  if (list.length === 0) {
    return (
      <div>
        <div style={{ fontFamily: fonts.mono, fontSize: 11, letterSpacing: 1.5, color: theme.textMute, marginBottom: 14, textTransform: "uppercase", fontWeight: 700 }}>
          {headingText}
        </div>
        <div style={{ border: `1px dashed ${theme.line}`, borderRadius: 10, padding: 20, color: theme.textMute, fontSize: 13 }}>
          {emptyText}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontFamily: fonts.mono, fontSize: 11, letterSpacing: 1.5, color: theme.textMute, marginBottom: 14, textTransform: "uppercase", fontWeight: 700 }}>
        {headingText}
      </div>
      <p className="small mb-3" style={{ color: theme.textMute }}>
        Visible only after you log in. Comments, scores, and proof are not shown on public or recruiter profiles.
      </p>
      <div className="d-flex flex-column gap-3">
        {proofError && (
          <div className="small mb-2" style={{ color: isLight ? "#DC2626" : "#F87171" }}>{proofError}</div>
        )}
        {list.map((r, idx) => {
          const hasFile = Boolean(r.proof_file);
          return (
            <div
              key={r.id || idx}
              className="card p-3 p-md-4 rounded-4 shadow-sm"
              style={{ background: theme.surface, border: `1px solid ${theme.line}`, color: theme.text }}
            >
              <div className="d-flex align-items-center gap-2 mb-3">
                <Lock size={14} color={theme.accent} />
                <strong className="small" style={{ color: theme.text }}>
                  {title}{r.company_name ? ` — ${r.company_name}` : ""}
                </strong>
                {r.created_at && (
                  <span className="small ms-auto" style={{ color: theme.textMute }}>
                    {new Date(r.created_at).toLocaleDateString()}
                  </span>
                )}
              </div>

              <div className="d-flex flex-wrap gap-2 mb-3">
                {criteria.map((c) => {
                  const val = r[c.key];
                  if (val == null || val === "") return null;
                  const color = scoreColor(val, theme);
                  return (
                    <span
                      key={c.key}
                      className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-3"
                      style={{
                        background: isLight ? "#F8FAFC" : "rgba(255,255,255,0.04)",
                        border: `1px solid ${theme.line}`,
                        fontSize: 11
                      }}
                    >
                      <c.icon size={11} color={theme.textMute} />
                      <span style={{ color: theme.textMute }}>{c.label}</span>
                      <strong style={{ color, fontFamily: fonts.mono }}>{Number(val).toFixed(0)}/10</strong>
                    </span>
                  );
                })}
              </div>

              {r.comment ? (
                <div
                  className="p-3 rounded-3 mb-3"
                  style={{ background: isLight ? "#F8FAFC" : "rgba(255,255,255,0.03)", border: `1px solid ${theme.line}` }}
                >
                  <div className="small fw-semibold text-uppercase mb-1" style={{ color: theme.textMute, letterSpacing: "0.06em", fontSize: 10 }}>
                    Private comment
                  </div>
                  <div className="small mb-0" style={{ color: theme.text, whiteSpace: "pre-wrap" }}>{r.comment}</div>
                </div>
              ) : (
                <p className="small mb-3" style={{ color: theme.textMute }}>No private comment was added with this rating.</p>
              )}

              {hasFile ? (
                <button
                  type="button"
                  onClick={() => viewProof(r.proof_file)}
                  disabled={proofBusy === r.proof_file}
                  className="btn btn-link p-0 d-inline-flex align-items-center gap-2 small fw-semibold text-decoration-none"
                  style={{ color: theme.accent }}
                >
                  <Paperclip size={14} />
                  {proofBusy === r.proof_file ? "Opening proof…" : `View private proof${r.proof_type ? ` (${r.proof_type})` : ""}`}
                </button>
              ) : (
                <span className="small" style={{ color: theme.textMute }}>No proof file attached.</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
